<template>
    <div :class="props.class">
        <ConfirmationModal v-if="askConfirmation" :title="confirmationTitle" :text="confirmationText" @emitResponse="checkConfirmationResponse" />
        <LoadingDialog v-if="savingModule" />
        <Dialog 
            v-model:visible="moduleSaveVisible" 
            modal 
            :header="selectedModule.id == null ? 'Criar módulo' : 'Editar módulo'" 
            :draggable="false" 
            :style="{ width: '50rem' }"
        >
            <div class="flex items-center gap-4 mb-4">
                <label for="title" class="font-semibold w-24">Título <RequiredMarker :isValid="dataToValidate.title" /></label>
                <InputText 
                    v-model="selectedModule.title" 
                    id="title" 
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                />
            </div>
            <div class="flex items-center gap-4 mb-8">
                <label for="description" class="font-semibold w-24">Descrição <RequiredMarker :isValid="dataToValidate.description" /></label>
                <InputText 
                    v-model="selectedModule.description" 
                    id="description" 
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                />
            </div>
            <div class="flex justify-end gap-2">
                <Button label="Cancel" severity="secondary" @click="moduleSaveVisible = false"></Button>
                <Button class="no-bg-button" label="Save" @click.prevent="saveModule()"></Button>
            </div>
        </Dialog>
        <div class="px-2" v-if="loadingModules">
            <LoaderComponent />
        </div>
        <div class="w-full text-left mb-5">
            <Button :label="'Novo módulo'" class="no-bg-button" @click.prevent="openSaveModule(null)" />
        </div>
        <div v-if="!loadingModules" class="draggable-list">
            <template v-for="(moduleData, index) of modulesData" :key="index">
                <div 
                    class="p-2 my-2 border border-iron rounded flex justify-between draggable-item text-white font-semibold flex-wrap flex-col md:flex-nowrap md:flex-row" 
                    :class="{ dragging: index === draggingIndex }"
                    @mousedown="onDragStart(index, $event)"
                    @mousemove="onDragMove($event)"
                    @mouseup="onDragEnd"
                >
                    <div class="w-full md:w-auto">
                        {{ moduleData.title }}
                    </div> 
                    <div class="flex flex-wrap justify-center md:justify-between w-full md:w-auto md:flex-row mt-5 mb:mt-0">
                        <Button
                            class="no-bg-button"
                            :icon="'pi pi-pen-to-square'"
                            @click="openSaveModule(moduleData.id)"
                            v-tooltip.top="'Editar módulo'" 
                        />
                        <Button
                            class="no-bg-button mx-3"
                            :icon="'pi pi-objects-column'"
                            v-tooltip.top="'Gerenciar vídeos'"
                            @click.prevent="openModuleVideoSection(moduleData.id)" 
                        />
                        <Button 
                            class="no-bg-button"
                            icon="pi pi-trash" 
                            v-tooltip.top="'Remover módulo'" 
                            @click.prevent="openConfirmationModal('removeCourseModule', moduleData.id)" 
                        />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
<script setup>
    import { defineProps, onMounted, defineEmits, reactive, ref, watch } from 'vue';
    import { useToast } from 'primevue/usetoast';
    import { showToast } from '@/composables/toast';
    import utils from '@/composables/utils';
    import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
    import Button from 'primevue/button';
    import Dialog from 'primevue/dialog';
    import InputText from 'primevue/inputtext';
    import RequiredMarker from '@/components/misc/RequiredMarkerComponent.vue';
    import { isTextValid } from '@/composables/textUtils';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import ConfirmationModal from '@/components/Utils/ConfirmationModal.vue';
    import CourseService from '@/services/CourseService';
    import CourseModuleService from '@/services/CourseModuleService';

    const toast = useToast();
    const props = defineProps({
        courseUuid: {
            type: Object,
            required: true
        },
        class: {
            type: String,
            required: false,
            default: ''
        }
    });

    const emit = defineEmits(["actionPerformed"]);

    let selectedModule = ref({
        id: null,
        title: null,
        description: null,
        timeInMinutes: 0
    });
    let savingModule = ref(false);
    let moduleSaveVisible = ref(false);
    let loadingModules = ref(true);
    let modulesData = ref([]);
    let moduleById = ref({});
    let courseUuid = ref(null);
    onMounted(() => {
        courseUuid.value = props.courseUuid;
        getModules();
    });

    function emitModuleSelected() {
        emit("moduleSelected", selectedModule.value);
    }
    
    function openModuleVideoSection(moduleId = ''){
        if(moduleById.value[moduleId] == undefined){
            showToast(toast, 'no module selected', 'error');
            return;
        }

        selectedModule.value = moduleById.value[moduleId];
        emitModuleSelected();
    }

    async function getModules(){
        loadingModules.value = true;

        let modules = await CourseService.getCourseModulesData(courseUuid.value);
        
        loadingModules.value = false;

        moduleById.value = {};
        modulesData.value = [];

        for(let mod of modules){
            modulesData.value.push(mod);
            moduleById.value[mod.id] = mod;
        }
    }

    watch(() => props.courseUuid, (newValue) => {
        courseUuid.value = newValue;
        getModules();
    },
        { deep: true }
    );

    async function openSaveModule(id = ''){
        moduleSaveVisible.value = true;
        if(moduleById.value[id] == undefined){
            resetSelectedModule();
            return;
        }
        selectedModule.value = moduleById.value[id];
    }

    let dataToValidate = reactive({
        title: true,
        description: true
    })
    let errorMessages = {
        title: 'Por favor informe o titulo do modulo',
        description: 'Por favor informe a descrição do modulo'
    }
    async function saveModule(){
        let isValid = true;
        for(let attr of Object.keys(dataToValidate)){
            if(!isTextValid(selectedModule.value[attr])){
                isValid = false;
                dataToValidate[attr] = false;
                showToast(toast, errorMessages[attr], 'error');
                break;
            }else{
                dataToValidate[attr] = true;
            }
        }
        if(!isValid){
            return;
        }
        let moduleValue = utils.copyVariable(selectedModule.value);
        delete moduleValue.files;
        moduleValue.courseUuid = courseUuid.value;
        savingModule.value = true;

        const response = await CourseModuleService.saveModule(moduleValue, moduleValue.id);

        savingModule.value = false;
        
        if(!response.success()){
            showToast(toast, response.getMessage(), 'error');
            return;
        }
        
        showToast(toast, moduleValue.id != null ? 'Módulo atualizado' : 'Módulo criado');
        moduleSaveVisible.value = false;
        getModules(true);
    }

    function resetSelectedModule(){
        selectedModule.value = {
            id: null,
            title: null,
            description: null,
            timeInMinutes: 0
        }
    }

    const draggingIndex = ref(null);
    const placeholderIndex = ref(null);
    const isDragging = ref(false);
    let preClickModulesData = ref();
    const onDragStart = (index) => {
        preClickModulesData.value = utils.jsonEncode(utils.copyVariable(modulesData.value));
        draggingIndex.value = index;
        isDragging.value = true;
        placeholderIndex.value = index;
        document.addEventListener('mousemove', onDragMove);
        document.addEventListener('mouseup', onDragEnd);
    };

    const onDragMove = (event) => {
        if (!isDragging.value) return;
        const { clientY } = event;
        const itemsArray = document.querySelectorAll('.draggable-item');
        itemsArray.forEach((item, index) => {
            const rect = item.getBoundingClientRect();
            if (clientY > rect.top && clientY < rect.bottom) {
                placeholderIndex.value = index;
            }
        });
    };

    const onDragEnd = () => {
        if (!isDragging.value) return;
        const movedItem = modulesData.value.splice(draggingIndex.value, 1)[0];
        modulesData.value.splice(placeholderIndex.value, 0, movedItem);
        draggingIndex.value = null;
        placeholderIndex.value = null;
        isDragging.value = false;
        document.removeEventListener('mousemove', onDragMove);
        document.removeEventListener('mouseup', onDragEnd);
        if(JSON.stringify(preClickModulesData.value) !== JSON.stringify(modulesData.value)) {
            persistModulesOrder();
        }
    };

    async function persistModulesOrder(){
        savingModule.value = true;
        let currModules = utils.copyVariable(modulesData.value);
        let reorderedModules = [];
        for(let thisModule of currModules){
            delete thisModule.files;
            reorderedModules.push(thisModule);
        }
        let dataToSend = {
            modules: reorderedModules,
            courseUuid: courseUuid.value
        }
        const response = await CourseModuleService.saveModuleOrder(dataToSend);  

        savingModule.value = false;

        if(!response.success()){
            showToast(toast, response.getMessage(), 'error');
            return;
        }

        showToast(toast, 'Order atualizada.');
    }

    let askConfirmation = ref(false);
    let selectedConfirmation = ref('');
    let confirmationMethodVariable = ref('');
    let confirmationTitle = ref('');
    let confirmationText = ref('');
    let confirmationModalValues = ref({
        removeCourseModule: {
            title: 'Are you sure you wish to remove this course module?',
            text: 'this action can not be undone'
        }
    })
    function openConfirmationModal(action = 'removeCourseModule', identifier = ''){
        confirmationTitle.value = confirmationModalValues.value[action].title;
        confirmationText.value = confirmationModalValues.value[action].text;
        selectedConfirmation.value = action;
        confirmationMethodVariable.value = identifier; 
        askConfirmation.value = true;
    }
    function checkConfirmationResponse(value){
        if([null,undefined].includes(value))
            return;
        const confirmation = value.confirmation;
        askConfirmation.value = false;
        if(confirmation){
            switch(selectedConfirmation.value){
                case 'removeCourseModule':
                    removeCourseModule(confirmationMethodVariable.value);
                break;
            }
        }
    }

    async function removeCourseModule(id = ''){
        savingModule.value = true;
        const response = await CourseModuleService.removeCourseModule(id);
        savingModule.value = false;

        if(!response.success()){
            showToast(toast, response.getMessage(), 'error');
            return;
        }
        showToast(toast, response.getMessage());
        getModules(true);
    }
</script>